/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CertCustomBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the certificates module.
----------------------------------------------------------------*/


import { FileText, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Button } from '../../../components/ui/button';
import { useI18n } from '../../../hooks/useI18n';
import { CertificateFormData } from '../types';

interface Props {
    formData: CertificateFormData;
    errors: Record<string, string>;
    showPrivateKey: boolean;
    validating: boolean;
    onChange: (field: keyof CertificateFormData, value: CertificateFormData[keyof CertificateFormData]) => void;
    onTogglePrivateKey: () => void;
    onValidate: () => void;
}

export function CertCustomBox({
    formData, errors, showPrivateKey, validating,
    onChange, onTogglePrivateKey, onValidate,
}: Props) {
    const { t } = useI18n();
    return (
        <FormBox title={t('certificates.sections.custom')} icon={<FileText className="h-5 w-5" />}>
            <div className="space-y-4">
                {/* Certificate PEM */}
                <div>
                    <Label htmlFor="certificate" required className="mb-2 block">
                        {t('certificates.fields.certificate')}
                    </Label>
                    <Textarea
                        id="certificate"
                        value={formData.certificate}
                        onChange={(e) => onChange('certificate', e.target.value)}
                        rows={6}
                        placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
                        className={`font-mono text-sm ${errors.certificate ? 'border-red-500' : ''}`}
                    />
                    {errors.certificate && <p className="text-red-500 text-sm mt-1">{errors.certificate}</p>}
                </div>

                {/* Private Key */}
                <div>
                    <Label htmlFor="privateKey" required className="mb-2 block">
                        {t('certificates.fields.privateKey')}
                    </Label>
                    <div className="relative">
                        <Textarea
                            id="privateKey"
                            value={formData.privateKey}
                            onChange={(e) => onChange('privateKey', e.target.value)}
                            rows={6}
                            placeholder={'-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----'}
                            className={`font-mono text-sm ${errors.privateKey ? 'border-red-500' : ''}`}
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-2 top-2"
                            onClick={onTogglePrivateKey}
                        >
                            {showPrivateKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                    </div>
                    {errors.privateKey && <p className="text-red-500 text-sm mt-1">{errors.privateKey}</p>}
                </div>

                {/* Certificate Chain */}
                <div>
                    <Label htmlFor="certificateChain" className="mb-2 block">
                        {t('certificates.fields.certificateChain')}
                    </Label>
                    <Textarea
                        id="certificateChain"
                        value={formData.certificateChain}
                        onChange={(e) => onChange('certificateChain', e.target.value)}
                        rows={4}
                        placeholder={t('certificates.placeholders.certificateChain')}
                        className="font-mono text-sm"
                    />
                </div>

                {/* Validate button */}
                {formData.certificate && (
                    <div className="flex justify-start">
                        <Button variant="outline" onClick={onValidate} disabled={validating}>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {t('certificates.actions.validate')}
                        </Button>
                    </div>
                )}
            </div>
        </FormBox>
    );
}
